import {nullable} from '@raison/io-ts-tools';
import {ServerValidationResult} from '@raison/network-facade';
import {AccountId, BooleanString, CryptoAddress, DateString, NonNegativeDoubleString, Uuid} from '@raison/types';
import {TypeOf, array, boolean, number, partial, record, string, type} from 'io-ts';

export type BaseRequest<T = undefined, TReturn = any> = (
    token: string | null,
    id: string,
    data?: T
) => Promise<TReturn>;

export const BuyCryptoRequest = type({
    account: number,
    amount: number,
});
export type BuyCryptoRequest = TypeOf<typeof BuyCryptoRequest>;

export type FeeRequest = {
    amount: NonNegativeDoubleString;
    address?: string;
};

export const SignedRequestValidationResult = ServerValidationResult.createCodecForType<SignedRequest>()(
    partial({
        uuid: array(string),
        tx: array(string),
    })
);
export const SignedRequest = type({
    uuid: Uuid,
    tx: string,
});
export type SignedRequest = TypeOf<typeof SignedRequest>;

export type SellCryptoRequest = {
    account: AccountId;
    amount: NonNegativeDoubleString;
    fee: number;
    all: BooleanString;
    repeated_event: BooleanString;
    two_fa_provider: 'otp' | 'sms';
};

export const SmsPin = type({
    uuid: Uuid,
    pin: string,
});
export type SmsPin = TypeOf<typeof SmsPin>;

export const OrderMessage = type({
    message: string,
    type: string,
});
export type OrderMessage = TypeOf<typeof OrderMessage>;

export const OrderUser = type({
    roles: array(string),
    uuid: string,
    username: string,
    email: string,
    status: string,
    created_at: DateString,
    invite_code: string,
});
export type OrderUser = TypeOf<typeof OrderUser>;

export const Order = type({
    from_value: nullable(string),
    to_value: nullable(string),
    status: string,
    status_human: string,
    is_moderate: boolean,
    is_completed: boolean,
    is_finished: boolean,
    is_rejected: boolean,
    messages: array(OrderMessage),
    value: string,
    id: number,
    user: OrderUser,
    created_at: DateString,
    updated_at: DateString,
    closed_at: nullable(DateString),
});
export type Order = TypeOf<typeof Order>;

export const TransactionHolder = type({
    uuid: Uuid,
    sign: array(number),
    tx: string,
    amount: NonNegativeDoubleString,
    key: string,
    two_fa_box_uuid: Uuid,
    tenant_uuid: Uuid,
    beneficiary: nullable(string),
    beneficiary_country: nullable(string),
    comment: nullable(string),
    expires_in: number,
});
export type TransactionHolder = TypeOf<typeof TransactionHolder>;

export const BlockchainFeeItem = type({
    description: string,
    type: string,
    fee: number,
    ratio: number,
    time: string,
});
export type BlockchainFeeItem = TypeOf<typeof BlockchainFeeItem>;

export const BlockchainFee = record(string, BlockchainFeeItem);
export type BlockchainFee = TypeOf<typeof BlockchainFee>;

export const Address = type({
    address: CryptoAddress,
});
export type Address = TypeOf<typeof Address>;
